import { Address } from "@redotech/account-common/address";
import { useRequiredContext } from "@redotech/react-util/context";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { getLocations } from "@redotech/redo-merchant-app-common/client/shopify";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { useQuery } from "@tanstack/react-query";
import { useContext } from "react";

/**
 * Hook to get available location options for return labels.
 * Fetches custom addresses and external platform locations.
 */
export function useLocationOptions() {
  const team = useContext(TeamContext);
  const client = useRequiredContext(RedoMerchantClientContext);
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);

  const enabled = !!team && !!client && !!rpcClient;

  return useQuery({
    queryKey: [
      "locationOptions",
      team?._id ?? null,
      team?.storeUrl ?? null,
    ] as const,
    queryFn: async ({ signal }) => {
      if (!team) {
        return [];
      }

      const [customAddressesResponse, externalLocationsResponse] =
        await Promise.all([
          rpcClient.getAddresses({ type: "custom" }, { signal }),
          getLocations(client, { storeUrl: team.storeUrl, signal }).catch(
            (error) => {
              console.error("Failed to fetch external locations:", error);
              return { locations: [] };
            },
          ),
        ]);

      const customAddresses: Address[] = customAddressesResponse.addresses.map(
        (addr) => ({
          name: addr.name,
          street1: addr.street1,
          street2: addr.street2 || undefined,
          city: addr.city,
          state: addr.state || undefined,
          zip: addr.zip,
          country: addr.country,
          phone: addr.phone || undefined,
          createdAt: addr.createdAt || new Date(),
          updatedAt: addr.updatedAt || new Date(),
        }),
      );

      const externalAddresses: Address[] =
        externalLocationsResponse.locations.map((loc) => ({
          name: loc.name,
          street1: loc.address.address1 || "",
          street2: loc.address.address2 || undefined,
          city: loc.address.city || "",
          state: loc.address.province || undefined,
          zip: loc.address.zip || "",
          country: loc.address.countryCode || "",
          phone: loc.address.phone || undefined,
          createdAt: new Date(),
          updatedAt: new Date(),
        }));

      return [...customAddresses, ...externalAddresses];
    },
    enabled,
  });
}
